import userDetail from "Stores/userDetail";
import onboardingCopilotStore from "Stores/onboardingCopilotStore";

/**
 * Decides whether the copilot should open in onboarding (new user) or digest
 * (existing user, first copilot visit) mode. New user = a workspace with no
 * data yet, the same signal DashboardRouting uses to land them on /copilot.
 * The digest fires once per member, tracked on the server record
 * (onboardingCopilotStore.digestShownAt) so it holds across browsers.
 */

// Dev/QA override: set localStorage sg_copilot_onboarding_force to "onboarding"
// or "digest" (or "1" for onboarding) to run that flow on ANY account,
// bypassing the new-vs-existing check and the once-only gates so it can be
// re-run repeatedly. Harmless in production unless the key is set by hand.
const forcedMode = () => {
  try {
    const v = localStorage.getItem("sg_copilot_onboarding_force");
    if (!v) return null;
    return v === "digest" ? "digest" : "onboarding";
  } catch (e) {
    return null;
  }
};

export const isNewWorkspace = () => {
  const ff = userDetail?.userFeatureFlag || {};
  return !((ff.noOfContactsUsed || 0) > 0 || (ff.noOfContactsAddedInTeam || 0) > 0);
};

// New-user onboarding only, no side effects. Used for the dashboard auto-open.
export const shouldAutoOpenOnboarding = () => {
  if (forcedMode() === "onboarding") return true;
  const memberId = userDetail?.userInfo?.memberId;
  if (!memberId) return false;
  return isNewWorkspace() && !!onboardingCopilotStore.shouldOffer(memberId);
};

// Full picker for an actual copilot visit: onboarding for new users, a one-time
// digest for existing users. Marks the digest as shown when it returns "digest".
export const pickCopilotOnboardingMode = () => {
  const forced = forcedMode();
  if (forced) return forced;
  const memberId = userDetail?.userInfo?.memberId;
  if (!memberId) return null;
  if (isNewWorkspace()) {
    // shouldOffer() is the only gate: it already returns null once the flow is
    // done or dismissed. There used to be a once-per-SESSION gate on top, to
    // stop the New chat button re-offering the flow -- but it could not tell a
    // New chat from a reload, so refreshing mid-onboarding dropped the user
    // into a plain chat with no way back. The New chat case is handled at the
    // click now (copilotStore.requestFreshChat), so a reload resumes properly.
    return onboardingCopilotStore.shouldOffer(memberId) ? "onboarding" : null;
  }
  onboardingCopilotStore.hydrate(memberId);
  // Unknown record (boot payload not in yet): never fire, so a slow load
  // cannot re-show the digest.
  if (!onboardingCopilotStore._hydrated) return null;
  if (!onboardingCopilotStore.digestShownAt) {
    onboardingCopilotStore.markDigestShown();
    return "digest";
  }
  return null;
};

export default { isNewWorkspace, shouldAutoOpenOnboarding, pickCopilotOnboardingMode };
